import React, { useState } from "react";
import "../styles/FinancialInfo.css";
import {
  getBsbError,
  getTfnError,
  getBsbErrorRequired,
  getTfnErrorRequired,
  formatBSB,
  formatAbnDigits,
  formatTfnDigits,
  getAbnError,
  getAbnErrorRequired,
} from "../utils/australianTaxValidators";

const FinancialInfo = ({ onNext, onPrevious, initialData = {} }) => {
  const isContractor =
    (initialData.memberType || "").toLowerCase() === "contractor" ||
    (initialData.role || "").toLowerCase() === "contractor";

  const [data, setData] = useState({
    bsb: initialData.bsb || "",
    accountNumber: initialData.accountNumber || "",
    accountName: initialData.accountName || "",
    taxFileNumber: initialData.taxFileNumber || "",
    abnNumber: initialData.abnNumber || "",
    visaStatus: initialData.visaStatus || "",
    otherVisaType: initialData.otherVisaType || "",
    visaExpiryDay: initialData.visaExpiryDay || "",
    claimTaxFreeThreshold: initialData.claimTaxFreeThreshold || "",
  });

  const [fieldErrors, setFieldErrors] = useState({
    bsb: "",
    taxFileNumber: "",
    abnNumber: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "bsb") {
      const formatted = formatBSB(value);
      setData((prev) => ({ ...prev, bsb: formatted }));
      setFieldErrors((prev) => ({ ...prev, bsb: getBsbError(formatted) || "" }));
    } else if (name === "abnNumber") {
      const formatted = formatAbnDigits(value);
      setData((prev) => ({ ...prev, abnNumber: formatted }));
      setFieldErrors((prev) => ({
        ...prev,
        abnNumber: getAbnError(formatted) || "",
      }));
    } else if (name === "taxFileNumber") {
      const formatted = formatTfnDigits(value);
      setData((prev) => ({ ...prev, taxFileNumber: formatted }));
      setFieldErrors((prev) => ({
        ...prev,
        taxFileNumber: getTfnError(formatted) || "",
      }));
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNext = () => {
    const bsbErr = getBsbErrorRequired(data.bsb);
    const tfnErr = !isContractor ? getTfnErrorRequired(data.taxFileNumber) : null;
    const abnErr = isContractor ? getAbnErrorRequired(data.abnNumber) : null;
    setFieldErrors({
      bsb: bsbErr || "",
      taxFileNumber: tfnErr || "",
      abnNumber: abnErr || "",
    });
    if (bsbErr || tfnErr || abnErr) {
      return;
    }
    onNext(data);
  };

  return (
    <div className="financial-info">
      <div className="form-row">
        <label>Bank Details</label>
        <div className="bank-details-row">
          <div className="financial-input-wrap">
            <input
              type="text"
              name="bsb"
              value={data.bsb}
              onChange={handleChange}
              placeholder="e.g. 062-000"
              maxLength={7}
              autoComplete="off"
              className={fieldErrors.bsb ? "financial-input-error" : ""}
              aria-invalid={!!fieldErrors.bsb}
              aria-describedby={fieldErrors.bsb ? "financial-bsb-error" : undefined}
            />
            {fieldErrors.bsb ? (
              <p id="financial-bsb-error" className="financial-field-error" role="alert">
                {fieldErrors.bsb}
              </p>
            ) : null}
          </div>
          <input
            type="text"
            name="accountNumber"
            value={data.accountNumber}
            onChange={handleChange}
            placeholder="Account Number"
            required
          />
        </div>
      </div>

      {isContractor && (
        <div className="form-row">
          <label>Account Name</label>
          <input
            type="text"
            name="accountName"
            value={data.accountName}
            onChange={handleChange}
            placeholder="Enter account name"
            required
          />
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Tax File Number</label>
          <div className="financial-input-wrap financial-input-wrap--full">
            <input
              type="text"
              name="taxFileNumber"
              value={data.taxFileNumber}
              onChange={handleChange}
              inputMode="numeric"
              placeholder="Enter TFN (9 digits)"
              maxLength={9}
              pattern="[0-9]{9}"
              autoComplete="off"
              className={fieldErrors.taxFileNumber ? "financial-input-error" : ""}
              aria-invalid={!!fieldErrors.taxFileNumber}
              aria-describedby={
                fieldErrors.taxFileNumber ? "financial-tfn-error" : undefined
              }
            />
            {fieldErrors.taxFileNumber ? (
              <p id="financial-tfn-error" className="financial-field-error" role="alert">
                {fieldErrors.taxFileNumber}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {isContractor && (
        <div className="form-row">
          <label>ABN Number</label>
          <input
            type="text"
            name="abnNumber"
            value={data.abnNumber}
            onChange={handleChange}
            inputMode="numeric"
            placeholder="Enter ABN Number (11 digits)"
            maxLength="11"
            pattern="[0-9]{11}"
            className={fieldErrors.abnNumber ? "financial-input-error" : ""}
            aria-invalid={!!fieldErrors.abnNumber}
            required
          />
          {fieldErrors.abnNumber ? (
            <p className="financial-field-error" role="alert">
              {fieldErrors.abnNumber}
            </p>
          ) : null}
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Visa Status</label>
          <select
            name="visaStatus"
            value={data.visaStatus}
            onChange={handleChange}
            required
          >
            <option value="">Select Visa Status</option>
            <option value="permanent-resident">Permanent Resident</option>
            <option value="student">Student</option>
            <option value="tourist">Tourist</option>
            <option value="citizen">Citizen</option>
            <option value="working-holiday">Working Holiday</option>
            <option value="others">Others</option>
          </select>
        </div>
      )}

      {!isContractor && data.visaStatus && data.visaStatus !== "citizen" && (
        <div className="form-row">
          <label>Expiry Date</label>
          <input
            type="date"
            name="visaExpiryDay"
            value={data.visaExpiryDay}
            onChange={handleChange}
            required
          />
        </div>
      )}

      {!isContractor && data.visaStatus === "others" && (
        <div className="form-row">
          <label>Visa Type</label>
          <input
            type="text"
            name="otherVisaType"
            value={data.otherVisaType}
            onChange={handleChange}
            placeholder="Enter visa type"
            required
          />
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Do you want to claim tax-free threshold from this payer?</label>
          <div className="tax-threshold-options">
            <label className="radio-option">
              <input
                type="radio"
                name="claimTaxFreeThreshold"
                value="yes"
                checked={data.claimTaxFreeThreshold === "yes"}
                onChange={handleChange}
                required
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="claimTaxFreeThreshold"
                value="no"
                checked={data.claimTaxFreeThreshold === "no"}
                onChange={handleChange}
              />
              <span>No</span>
            </label>
          </div>
        </div>
      )}

      <div className="form-buttons">
        <button className="financial-back-btn" onClick={onPrevious}>
          Back
        </button>
        <button className="financial-next-btn" onClick={handleNext}>
          Next
        </button>
      </div>
    </div>
  );
};

export default FinancialInfo;
